import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import {
  LayoutDashboard, Shield, BookOpen, Video, Menu,
} from 'lucide-react';

/**
 * PhoneBottomTabBar — EMSPCR Foundation v1.0 §7.2.
 *
 * "Functional mobile" layout for < 768px viewports. Replaces the
 * hamburger-overlay-only navigation with a rackmount-footer bottom
 * tab bar: five primary destinations always one thumb-tap away.
 *
 * Kept intentionally short (5 tabs per iOS HIG) and icon-only with
 * a 10px label so the bar stays at 56pt total height without eating
 * content real estate. Full nav is still reachable via the Menu
 * hamburger, so every secondary destination (Admin, Roster, EAP, etc.)
 * continues to be addressable from a phone.
 *
 * Hidden above the iPad breakpoint — from 768px up the side nav is
 * the primary navigation affordance.
 */

interface Tab {
  path: string;
  label: string;
  match: (pathname: string) => boolean;
  icon: typeof LayoutDashboard;
}

// Phone bottom-tab nav. Pared down per the simplified-flow brief:
// the home page IS the report list, so the redundant "New" and
// "Reports" tabs are gone. Home + QA + the menu drawer (which holds
// admin/roster/EAP for users with those roles) cover the surface.
const PRIMARY_TABS: Tab[] = [
  { path: '/',           label: 'Reports',   match: (p) => p === '/' || p.startsWith('/incidents'), icon: LayoutDashboard },
  { path: '/telehealth', label: 'Telehealth', match: (p) => p.startsWith('/telehealth'),            icon: Video },
  { path: '/protocols',  label: 'Protocols',  match: (p) => p.startsWith('/protocols'),             icon: BookOpen },
  { path: '/qa',         label: 'QA',         match: (p) => p.startsWith('/qa'),                    icon: Shield },
];

interface Props {
  onOpenMenu: () => void;
}

export function PhoneBottomTabBar({ onOpenMenu }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <nav
      // Hidden from iPad up. Fixed bottom; 56pt tall; dark command-strip
      // finish so it reads as "rackmount footer" per §8.10.
      className={cn(
        'ipad:hidden',
        'fixed bottom-0 inset-x-0 z-40',
        'border-t border-slate-700 bg-slate-950/95 backdrop-blur',
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Primary navigation"
    >
      <div className="grid grid-cols-5">
        {PRIMARY_TABS.map((tab) => {
          const active = tab.match(location.pathname);
          const Icon = tab.icon;
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={cn(
                'relative flex flex-col items-center justify-center gap-0.5 py-2 transition-colors min-h-[44px]',
                active
                  ? 'text-amber-300'
                  : 'text-slate-400 hover:text-slate-200',
              )}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={20} strokeWidth={active ? 2.2 : 1.7} />
              <span
                className={cn(
                  'text-[10px] font-bold uppercase tracking-widest',
                  active && 'text-amber-300',
                )}
              >
                {tab.label}
              </span>
              {active && (
                <span
                  className="absolute top-0 h-0.5 w-8 rounded-full bg-amber-400"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
        <button
          onClick={onOpenMenu}
          className="flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 hover:text-slate-200 transition-colors"
          aria-label="Open full menu"
        >
          <Menu size={20} strokeWidth={1.7} />
          <span className="text-[10px] font-bold uppercase tracking-widest">Menu</span>
        </button>
      </div>
    </nav>
  );
}
